import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input, Avatar } from 'antd';
import {
  SearchOutlined,
  FolderOpenOutlined,
  CheckSquareOutlined,
  UserOutlined,
  ArrowRightOutlined,
  EnterOutlined,
} from '@ant-design/icons';
import PropTypes from 'prop-types';
import { useUserContext } from '../../contexts/UserContext';
import SearchService from '../../services/SearchService';
import { NAV_GROUPS } from './navConfig.jsx';
import styles from './CommandPalette.module.scss';

// Build flat nav list with group context
const buildNavItems = (check) =>
  NAV_GROUPS.flatMap((group) =>
    group.items
      .filter((item) => !item.permission || check(item.permission))
      .map((item) => ({ ...item, group: group.label }))
  );

const TYPE_ICON = {
  affaire: <FolderOpenOutlined />,
  tache: <CheckSquareOutlined />,
  user: <UserOutlined />,
};

export default function CommandPalette({ open, onClose }) {
  const navigate = useNavigate();
  const { check } = useUserContext();
  const [query, setQuery] = useState('');
  const [apiResults, setApiResults] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const debounceRef = useRef(null);

  const navItems = useMemo(() => buildNavItems(check), [check]);

  const filteredNav = useMemo(() => {
    if (!query) return navItems.slice(0, 8);
    const q = query.toLowerCase();
    return navItems.filter(
      (i) =>
        i.label.toLowerCase().includes(q) ||
        i.group.toLowerCase().includes(q)
    );
  }, [query, navItems]);

  // Debounced API search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.length < 2) { setApiResults([]); return; }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await SearchService.getInstance().search(query);
        setApiResults(res.data || []);
      } catch {
        setApiResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  // Reset on open/close
  useEffect(() => {
    if (open) {
      setQuery('');
      setApiResults([]);
      setActiveIndex(0);
      setTimeout(() => inputRef.current?.focus(), 60);
    }
  }, [open]);

  const allItems = useMemo(() => {
    const nav = filteredNav.map((i) => ({ ...i, _kind: 'nav' }));
    const api = apiResults.map((i) => ({ ...i, _kind: 'api' }));
    return [...nav, ...api];
  }, [filteredNav, apiResults]);

  const goToItem = useCallback((item) => {
    if (!item) return;
    if (item._kind === 'nav') navigate(item.to);
    else {
      const paths = { user: '/user', tache: '/tache', affaire: '/affaire' };
      navigate(`${paths[item.type] || '/'}/${item.uid}`);
    }
    onClose();
  }, [navigate, onClose]);

  // Keyboard navigation
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, allItems.length - 1));
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        goToItem(allItems[activeIndex]);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, allItems, activeIndex, goToItem, onClose]);

  // Scroll active item into view
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-index="${activeIndex}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  if (!open) return null;

  const navSection = allItems.filter((i) => i._kind === 'nav');
  const apiSection = allItems.filter((i) => i._kind === 'api');

  return (
    <div className={styles.backdrop} onClick={onClose}>
      <div className={styles.panel} onClick={(e) => e.stopPropagation()}>
        {/* Search input */}
        <div className={styles.inputWrap}>
          <SearchOutlined className={styles.searchIcon} />
          <input
            ref={inputRef}
            className={styles.input}
            placeholder="Rechercher ou naviguer… (↑↓ Entrée)"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setActiveIndex(0); }}
          />
          <kbd className={styles.escKbd}>Esc</kbd>
        </div>

        {/* Results */}
        <div className={styles.results} ref={listRef}>
          {/* Nav items */}
          {navSection.length > 0 && (
            <div className={styles.section}>
              <p className={styles.sectionLabel}>
                {query ? 'Navigation' : 'Accès rapide'}
              </p>
              {navSection.map((item, idx) => (
                <button
                  key={item.to}
                  data-index={idx}
                  className={`${styles.item} ${idx === activeIndex ? styles.itemActive : ''}`}
                  onMouseEnter={() => setActiveIndex(idx)}
                  onClick={() => goToItem(item)}
                >
                  <span className={styles.itemIcon}>{item.icon}</span>
                  <span className={styles.itemInfo}>
                    <span className={styles.itemLabel}>{item.label}</span>
                    <span className={styles.itemMeta}>{item.group}</span>
                  </span>
                  {item.shortcut && (
                    <kbd className={styles.shortcut}>{item.shortcut}</kbd>
                  )}
                  <EnterOutlined className={`${styles.enterIcon} ${idx === activeIndex ? styles.enterIconVisible : ''}`} />
                </button>
              ))}
            </div>
          )}

          {/* API results */}
          {apiSection.length > 0 && (
            <div className={styles.section}>
              <p className={styles.sectionLabel}>Résultats</p>
              {apiSection.map((item, i) => {
                const idx = navSection.length + i;
                return (
                  <button
                    key={item.uid}
                    data-index={idx}
                    className={`${styles.item} ${idx === activeIndex ? styles.itemActive : ''}`}
                    onMouseEnter={() => setActiveIndex(idx)}
                    onClick={() => goToItem(item)}
                  >
                    {item.avatar ? (
                      <Avatar src={item.avatar} size={24} className={styles.apiAvatar} />
                    ) : (
                      <span className={styles.itemIcon}>{TYPE_ICON[item.type] || <ArrowRightOutlined />}</span>
                    )}
                    <span className={styles.itemInfo}>
                      <span className={styles.itemLabel}>{item.name}</span>
                      <span className={styles.itemMeta}>{item.type}</span>
                    </span>
                    <EnterOutlined className={`${styles.enterIcon} ${idx === activeIndex ? styles.enterIconVisible : ''}`} />
                  </button>
                );
              })}
            </div>
          )}

          {loading && (
            <p className={styles.loading}>Recherche en cours…</p>
          )}

          {!loading && query.length >= 2 && allItems.length === 0 && (
            <p className={styles.empty}>Aucun résultat pour &laquo;{query}&raquo;</p>
          )}
        </div>

        {/* Footer hints */}
        <div className={styles.footer}>
          <span><kbd>↑</kbd><kbd>↓</kbd> Naviguer</span>
          <span><kbd>↵</kbd> Ouvrir</span>
          <span><kbd>Esc</kbd> Fermer</span>
        </div>
      </div>
    </div>
  );
}

CommandPalette.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};
